import React, { useState } from 'react';

import CountryItem from '../CountryItem/';
import { europeCountryMapPath, europeCountryList } from '../EuropeanCountrySourceConfig';
import { DataSource } from '~/Api/DataSource';

import '../tailwind.css';

interface DataSourceList {
	dataSource: DataSource;
	lastUpdateAt: Date;
	type: string;
}

interface GrouppedCountryDataSources {
	european: DataSourceList[];
	world: DataSourceList[];
}

interface SourceList {
	datasource: DataSourceList[];
}

function groupByWorld(array: DataSourceList[], groupByName: (value: DataSourceList) => string) {
	const map = new Map<string, DataSourceList[]>();
	array.forEach((item) => {
		const key = groupByName(item);
		const collection = map.get(key);
		if (!collection) {
			map.set(key, [item]);
		} else {
			collection.push(item);
		}
	});

	return map;
}

function groupByEurope(array: DataSourceList[], groupByName: (value: DataSourceList) => string) {
	const map = new Map<string, DataSourceList[]>();
	array.forEach((item) => {
		const key = groupByName(item);
		const collection = map.get(key);
		if (!collection) {
			map.set(key, [item]);
		} else {
			collection.push(item);
		}
	});

	return map;
}

function groupByCountryType(
	array: DataSourceList[],
	isEuropeanCountry: (value: DataSourceList) => boolean
): GrouppedCountryDataSources {
	return {
		european: array.filter((value: DataSourceList) => isEuropeanCountry(value)),
		world: array.filter((value: DataSourceList) => !isEuropeanCountry(value)),
	};
}

export default function DataSourceMap(sourcelist: SourceList) {
	const [showMap, setShowMap] = useState(true);
	const [selectEuState, setSelectEuState] = useState('');
	const upToDateCountryArray: string[] = [];

	const handleMapClick = (event: React.MouseEvent<SVGPathElement, MouseEvent>) => {
		event.preventDefault();
		setSelectEuState(event.currentTarget.id);
	};

	const handleViewMapClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();

		setShowMap(true);
		return;
	};

	const handleViewListClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();

		setShowMap(false);
		return;
	};

	const countryList = groupByCountryType(sourcelist.datasource, (i) =>
		europeCountryList.some((el) => el === i.dataSource.country.name)
	);

	const groupListWorldArray = groupByWorld(countryList.world, (value) => value.dataSource.country.name);
	const groupListEuropeArray = groupByEurope(countryList.european, (value) => value.dataSource.country.name);

	Array.from(groupListEuropeArray.entries()).map((entry) => {
		const [name, value] = entry;
		value.map((datasource) => {
			if (datasource.type !== 'up-to-date') {
				upToDateCountryArray.push(name);
			}
		});
	});

	return (
		<div className='tailwind-content'>
			<div className='px-10 py-10'>
				<div className='w-full max-w-7xl mx-auto'>
					<div className='flex justify-between items-end mb-8'>
						<div className='max-w-lg'>
							<h2 className='mb-1 flex justify-start items-center font-display text-h2 text-cPurple-800 font-semibold'>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									fill='none'
									viewBox='0 0 24 24'
									strokeWidth='1.5'
									stroke='currentColor'
									className='w-8 h-8 mr-2'
								>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										d='M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-16.5-3a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3m-19.5 0a4.5 4.5 0 0 1 .9-2.7L5.737 5.1a3.375 3.375 0 0 1 2.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 0 1 .9 2.7m0 0a3 3 0 0 1-3 3m0 3h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008Zm-3 6h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008Z'
									/>
								</svg>
								Datasources status
							</h2>
							<p className='text-md text-gray-500'>
								Analytics module covers all original, orphan and biosimilar EMA authorized products. This dashboard
								provides information about the global data coverage.
							</p>
						</div>
						<div className='flex items-center border border-gray-200'>
							<a
								href='#'
								id='map'
								onClick={handleViewMapClick}
								className={`inline-flex p-2.5 border-r border-cPurple-800 transition ${
									showMap
										? 'text-white bg-cPurple-600 cursor-default'
										: 'text-gray-400 bg-white hover:text-white hover:bg-cPurple-600 cursor-pointer'
								}`}
							>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									fill='none'
									viewBox='0 0 24 24'
									strokeWidth='1.5'
									stroke='currentColor'
									className='w-5 h-5 pointer-events-none'
								>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										d='M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z'
									/>
								</svg>
							</a>
							<a
								href='#'
								id='list'
								onClick={handleViewListClick}
								className={`inline-flex p-2.5 transition ${
									!showMap
										? 'text-white bg-cPurple-600 cursor-default'
										: 'text-gray-400 bg-white hover:text-white hover:bg-cPurple-600 cursor-pointer'
								}`}
							>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									fill='none'
									viewBox='0 0 24 24'
									strokeWidth='1.5'
									stroke='currentColor'
									className='w-5 h-5 pointer-events-none'
								>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										d='M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z'
									/>
								</svg>
							</a>
						</div>
					</div>
					{showMap && (
						<div className='flex justify-between items-stretch'>
							<div className='w-3/4'>
								<h4 className='mb-2 flex justify-start items-center font-display text-h2-head text-cPurple-800 font-semibold tracking-tight'>
									<svg
										xmlns='http://www.w3.org/2000/svg'
										fill='none'
										viewBox='0 0 24 24'
										strokeWidth='1.5'
										stroke='currentColor'
										className='w-7 h-7 mr-2 text-cPurple-500'
									>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											d='m20.893 13.393-1.135-1.135a2.252 2.252 0 0 1-.421-.585l-1.08-2.16a.414.414 0 0 0-.663-.107.827.827 0 0 1-.812.21l-1.273-.363a.89.89 0 0 0-.738 1.595l.587.39c.59.395.674 1.23.172 1.732l-.2.2c-.212.212-.33.498-.33.796v.41c0 .409-.11.809-.32 1.158l-1.315 2.191a2.11 2.11 0 0 1-1.81 1.025 1.055 1.055 0 0 1-1.055-1.055v-1.172c0-.92-.56-1.747-1.414-2.089l-.655-.261a2.25 2.25 0 0 1-1.383-2.46l.007-.042a2.25 2.25 0 0 1 .29-.787l.09-.15a2.25 2.25 0 0 1 2.37-1.048l1.178.236a1.125 1.125 0 0 0 1.302-.795l.208-.73a1.125 1.125 0 0 0-.578-1.315l-.665-.332-.091.091a2.25 2.25 0 0 1-1.591.659h-.18c-.249 0-.487.1-.662.274a.931.931 0 0 1-1.458-1.137l1.411-2.353a2.25 2.25 0 0 0 .286-.76m11.928 9.869A9 9 0 0 0 8.965 3.525m11.928 9.868A9 9 0 1 1 8.965 3.525'
										/>
									</svg>
									Europe
								</h4>
								<div className='flex justify-between items-stretch'>
									<div className='w-1/3 pr-3 relative'>
										{selectEuState == '' && (
											<p className='sticky top-2 left-0 px-3 py-2 rounded text-md text-cPurple-800 bg-gray-200 tracking-wide'>
												<strong>TIP:</strong> You can select a country by clicking on the map.
											</p>
										)}
										<ul className='sticky top-2 left-0 bg-gray-50 list-none'>
											{Array.from(groupListEuropeArray.entries()).map((entry) => {
												const [name, list] = entry;
												if (selectEuState == name.replace(/[\s,]+/g, '-').toLowerCase()) {
													return (
														<CountryItem
															key={name.replace(/[\s,]+/g, '-').toLowerCase()}
															name={name}
															sourcelist={list}
															variant='white'
														/>
													);
												}
											})}
										</ul>
									</div>
									<div className='relative w-2/3 map-analytics'>
										<svg preserveAspectRatio='xMidYMid meet' viewBox='0 0 112 108'>
											{europeCountryMapPath.map(({ name, path }) => {
												let upToDateCountry = true;
												upToDateCountryArray
													.filter((d) => d == name)
													.map(() => {
														upToDateCountry = false;
													});

												return (
													<path
														key={name}
														fillRule='nonzero'
														stroke='#FFFFFF'
														strokeWidth='0.2'
														fill={upToDateCountry ? '#262955' : '#ea580c'}
														className={`map-item ${
															name.replace(/[\s,]+/g, '-').toLowerCase() == selectEuState ? 'map-item-active' : ''
														}`}
														d={path}
														id={name.replace(/[\s,]+/g, '-').toLowerCase()}
														onClick={handleMapClick}
													></path>
												);
											})}
											<path
												fillRule='nonzero'
												stroke='#FFFFFF'
												strokeWidth='0.2'
												d='M66.7464536,85.4703349 C66.66619,85.0640019 67.9643662,83.198881 68.2889102,83.198881 C68.5593636,83.198881 68.6605655,81.4052607 68.5454047,80.4740082 C67.5394926,79.7781847 66.7037045,79.5418837 66.7037045,79.5418837 C66.7037045,79.5418837 63.9982984,79.4093459 63.5489969,79.2279784 C63.097078,79.0474828 62.6460316,79.814807 62.2403516,79.4991577 C61.835544,79.1852523 61.247526,79.5889695 61.3391312,80.0397725 C61.4298639,80.4897034 62.2857179,80.7617547 62.2857179,81.4366512 C62.2857179,82.1124197 63.9991709,83.1047094 63.9546769,83.4648285 C63.922397,83.7176967 64.9815273,84.4048007 65.6131667,85.3316934 C65.9507972,85.5435795 66.3084936,85.8199906 66.6618279,86.1051213 C66.7316223,85.8060392 66.7673919,85.5732261 66.7464536,85.4703349 Z'
												className='shape'
											></path>
											<path
												fillRule='nonzero'
												stroke='#FFFFFF'
												strokeWidth='0.2'
												d='M70.6575582,92.151287 C71.1103495,92.0614752 70.8381512,91.6996121 71.1557158,91.3822189 C71.4715356,91.0665696 71.8327217,90.4326552 71.8798329,90.2059458 C71.8850675,90.1815309 71.9016437,90.1640918 71.9234544,90.1492684 C71.3092636,89.688002 70.878283,89.3566575 70.7421839,88.8823116 C70.60434,88.3983742 70.7465461,87.4531703 70.9629088,86.7643225 C70.6121918,86.5149421 69.5321232,85.768545 69.1936203,85.768545 C68.8586071,85.768545 68.341256,86.5140702 68.2138812,87.2308207 C69.0060478,87.652849 69.3183778,87.953675 68.7696192,88.5012654 C68.1981774,89.0750146 68.0760372,91.0055325 68.7094215,91.5182445 C69.3131432,92.0074137 69.4867569,92.9770324 70.0704127,93.2525716 C70.2335571,92.7668902 70.3583146,92.214068 70.6575582,92.151287 Z'
												className='shape'
											></path>
											<path
												fillRule='nonzero'
												stroke='#FFFFFF'
												strokeWidth='0.2'
												d='M71.9234544,90.1492684 L71.9234544,90.1492684 C71.9426479,90.1344451 71.9696932,90.1248536 72.0011007,90.115262 C72.0072077,90.1143901 72.0106974,90.1117742 72.0194217,90.1100303 C72.0508292,90.1021826 72.090961,90.0969509 72.1328377,90.0934631 C72.1389447,90.0908472 72.1450517,90.0908472 72.1502863,90.0908472 C72.3256447,90.0751519 72.5620733,90.0856154 72.8046089,90.0777678 C72.8194402,90.0768958 72.8368888,90.0768958 72.8543374,90.0768958 C72.9093006,90.0742799 72.9607739,90.0699201 73.0166095,90.0664323 C73.0366754,90.0629445 73.0593585,90.0629445 73.0802969,90.0603286 C73.1500913,90.050737 73.2198857,90.0402735 73.2844455,90.0228343 C73.8270971,89.8885526 73.9640687,89.2999801 74.234522,89.4342618 C74.5075927,89.5694155 75.9566993,88.831738 76.1372923,88.5605586 C76.1608479,88.5282961 76.1721895,88.4986495 76.1756792,88.4681309 C76.2053419,88.2597327 75.8153656,88.1350425 75.7752338,87.8961256 C75.7298674,87.6258183 74.7797909,86.7189806 74.6428194,86.5838269 C74.6149016,86.5550523 74.60356,86.5044786 74.5983254,86.4443134 C74.5983254,86.4382097 74.5948357,86.432106 74.5948357,86.4268742 C74.591346,86.3623493 74.5948357,86.2873608 74.60356,86.2053966 C73.1867334,86.410307 71.0859214,86.8480306 71.0859214,86.8480306 C71.0859214,86.8480306 71.034448,86.8096644 70.9646536,86.7625786 C70.7482909,87.4514264 70.6060848,88.3966303 70.7439288,88.8805677 C70.8774106,89.3566575 71.3092636,89.688002 71.9234544,90.1492684 Z'
												className='shape'
											></path>
											<path
												fillRule='nonzero'
												stroke='#FFFFFF'
												strokeWidth='0.2'
												d='M106.616514,73.2768562 C106.932334,73.2323863 107.884155,72.1903949 108.20172,72.1903949 C108.518412,72.1903949 109.876786,72.4633182 109.876786,72.2374807 C109.876786,72.0098994 110.331322,71.2399593 110.237972,71.0132499 C110.148111,70.7882844 109.696193,70.4726351 109.786053,70.2450538 C109.876786,70.0192163 110.51017,70.0192163 110.51017,69.747165 C110.51017,69.4751137 110.012885,69.3390881 110.012885,69.1577205 C110.012885,68.9780969 110.647141,68.6607037 110.600903,68.2988406 C110.555536,67.9378495 110.012885,67.8907637 109.423994,67.5742425 C108.835104,67.2559773 108.244469,67.3928749 107.701817,67.1216956 C107.159166,66.8505162 106.025879,67.0310118 106.025879,66.8505162 C106.025879,66.6700207 105.302634,66.0805762 105.120296,65.763183 C104.939703,65.4475338 104.350813,65.6271574 103.988754,65.8538668 C103.625823,66.0814482 103.308259,66.17126 102.947073,65.9454225 C102.585014,65.7178411 102.268322,65.355978 101.950757,65.4911317 C101.633193,65.6262854 101.044302,65.5373456 101.180401,65.0821828 C101.315628,64.6296359 100.592383,63.6356024 100.274819,63.5004487 C99.9581266,63.3635511 99.0063053,63.5902605 98.7803458,63.1385856 C98.5526416,62.6842948 98.1452167,62.3215597 98.3266821,62.0067824 C98.5064028,61.6893892 98.2813158,60.8278934 98.0544839,60.4660303 C97.8267796,60.1050392 96.6961101,60.1503811 96.3794179,60.2855348 C96.0627258,60.4206885 95.3368638,60.1503811 95.0192992,60.3317486 C94.7017346,60.5131161 94.522014,60.8278934 94.0683503,60.8278934 C93.816218,60.8278934 93.647839,60.794759 93.509995,60.5950803 C93.0196892,60.815686 92.1969875,60.8601559 92.1969875,60.8601559 C92.1969875,60.8601559 90.9869268,61.8245429 90.9869268,62.186406 C90.9869268,62.5473972 90.9293464,63.1516649 90.6257407,62.850839 C90.3238799,62.5491411 89.0562388,62.5473972 88.7543779,62.7305086 C88.4507722,62.9110042 87.9691907,62.0059104 87.4256667,62.3076084 C86.8830151,62.6093063 86.1580256,61.8855801 86.0960831,62.186406 C86.0367578,62.4898478 85.2515707,62.2465712 84.7080466,62.0059104 C84.1653951,61.7643777 83.1987424,62.1271128 82.8358114,61.7033406 C82.4728805,61.2795684 80.1181913,61.2804403 79.3330042,61.2804403 C78.547817,61.2804403 78.3672239,61.7033406 78.0042929,62.0652037 C77.6771316,62.3939323 77.2976245,62.3267915 76.9120103,62.3084803 C76.9460351,62.6136661 77.0053604,62.8848454 77.0830067,62.999944 C77.2635997,63.2719953 78.1246883,64.5842941 77.8978565,64.992371 C77.671897,65.399576 77.2635997,65.399576 76.9931463,65.7160972 C76.7209481,66.0326184 75.1357424,67.4373449 75.0903761,67.6614384 C75.0450097,67.8881478 75.3634467,68.8839253 75.1357424,69.0652928 C74.9106554,69.2457884 74.2292875,70.197096 74.2292875,70.5580871 C74.2292875,70.9208222 74.7283175,71.418711 75.1357424,71.5547366 C75.3067388,71.6131579 75.4175374,71.6942501 75.4681383,71.7901656 C75.7848305,71.7465677 76.0744773,71.7212808 76.254198,71.7212808 C76.5560588,71.7212808 77.88477,72.0857598 78.3052814,72.0857598 C78.7292824,72.0857598 79.3338766,72.2653834 79.5153421,72.446751 C79.69768,72.6272465 80.1181913,72.5051722 80.4209246,72.2043463 C80.7236579,71.9043923 81.0857164,72.0857598 81.4486474,72.0857598 C81.8089611,72.0857598 82.4144276,71.781446 82.5950207,71.481492 C82.7756137,71.181538 84.5885237,70.6967287 85.0701052,70.6348196 C85.5534315,70.5763983 86.278421,71.5399133 86.7006772,71.5399133 C87.1246783,71.5399133 87.9081206,71.8433551 88.1506562,72.6272465 C88.3923194,73.4120099 89.1164365,74.3790127 89.4182973,74.6780948 C89.7192857,74.9815366 89.9007512,75.765428 89.9007512,76.0069607 C89.9007512,76.2476215 89.2368318,75.9467955 88.5109699,75.765428 C87.7885977,75.5840605 87.7266552,76.3095306 87.7266552,76.6705218 C87.7266552,77.0332568 86.7006772,78.3002137 86.5192117,78.5417464 C86.3377463,78.7841511 86.8830151,78.9637747 87.3043989,79.0239399 C87.4841196,79.0492267 88.2108539,78.5417464 88.4507722,78.5417464 C88.5493568,78.5417464 88.8494728,78.7745595 89.1713996,79.0483547 C89.1024776,78.1040228 89.297902,78.1572123 90.1406695,77.5171942 C91.0174619,76.8536332 91.1073222,75.615451 91.8925094,75.4646021 C92.6776966,75.3128811 93.00922,74.9501461 94.1573382,75.1306417 C95.3045839,75.3128811 93.585024,76.0357354 94.3675939,76.4586357 C95.1536535,76.8824079 97.5694127,76.3383053 97.6906805,76.9417011 C97.8110759,77.5450969 94.9111179,78.1502367 94.9730604,78.4519346 C95.0332581,78.7527605 96.5146446,79.0544585 97.0267611,79.384931 C97.5406225,79.7188914 96.8758307,80.6527598 97.1174939,80.9562017 C97.359157,81.2578996 98.1434718,81.0146229 98.7777286,80.562948 C99.4119853,80.1095292 99.9232294,80.2603782 100.739824,79.7188914 C101.556419,79.1747888 103.246316,79.6569823 103.457444,79.3265098 C103.6677,78.9942933 102.973245,78.6620768 101.403743,78.7841511 C99.8342415,78.9036095 99.7435088,77.4247665 99.7435088,77.1221967 C99.7435088,76.8213707 101.374081,75.5831885 102.309326,75.4951206 C103.246316,75.4035649 104.000096,75.1306417 104.906551,74.7112292 C105.271227,74.5411972 105.912463,74.3676773 106.566785,74.2429871 C106.472563,73.8183429 106.346061,73.3160944 106.616514,73.2768562 Z'
												className='shape'
											></path>
											<path
												fillRule='nonzero'
												stroke='#FFFFFF'
												strokeWidth='0.2'
												d='M94.2524331,56.0757153 C93.8903746,55.9850316 93.6635427,55.1723655 93.0729074,54.9447841 C92.4848895,54.7172027 92.6209886,53.9934765 92.122831,53.6769553 C91.6255457,53.3595621 91.940493,52.7265197 91.8959991,52.227759 C91.8506327,51.7307422 91.9867318,50.9608021 91.715406,50.8256484 C91.4423354,50.6896228 90.3107934,50.0557084 89.8135082,50.2832898 C89.3144781,50.5091272 88.9524196,50.6896228 88.7247153,50.2832898 C88.4987559,49.8743409 88.1829361,49.6485034 87.6847785,49.7836571 C87.1857484,49.9205547 86.8681838,49.6938453 86.596858,49.4671359 C86.5567262,49.4322575 86.521829,49.378196 86.4939113,49.3180308 C86.2932523,49.3598849 86.028906,49.4374893 85.8587821,49.5743869 C85.5551764,49.8159196 85.4958511,50.2981131 85.0727225,50.2981131 C84.6504663,50.2981131 84.1680123,50.1167455 83.684686,50.4794806 C83.2004872,50.8413437 83.0800919,51.2651159 83.141162,51.5650699 C83.2004872,51.8667678 83.2615574,52.3507052 82.8994988,52.3507052 C82.5365679,52.3507052 82.597638,52.7736055 82.2355794,52.8930639 C81.8726485,53.0142663 81.2689268,53.5583689 81.2078567,53.8565789 C81.1485314,54.1600208 81.1485314,54.8244537 80.9051234,54.8244537 C80.6643326,54.8244537 79.8189478,54.9447841 79.5772846,55.2456101 C79.3364939,55.5490519 79.0363779,55.8481339 78.5513067,55.7295475 C78.3035365,55.6667664 77.6396171,55.6379918 77.0550889,55.6231684 C77.0638132,55.6824617 77.0638132,55.7417549 77.0402576,55.803664 C76.8142981,56.4393223 76.9948912,57.025279 77.3569497,57.3426722 C77.7198807,57.6591934 77.8559798,58.9270222 77.4476825,59.1083897 C77.0393851,59.2880133 76.2699017,59.8321159 76.2699017,60.1495091 C76.2699017,60.4651584 77.0847515,60.7380816 76.9486524,61.1897565 C76.8718785,61.4496004 76.8683888,61.909123 76.9128828,62.3084803 C77.2984969,62.3267915 77.6780041,62.3939323 78.0051654,62.0652037 C78.3680963,61.7033406 78.5495618,61.2804403 79.3338766,61.2804403 C80.1181913,61.2804403 82.4746253,61.2804403 82.8366839,61.7033406 C83.1987424,62.1262408 84.1653951,61.7635058 84.7089191,62.0059104 C85.2524431,62.2465712 86.0367578,62.4898478 86.0969555,62.186406 C86.1588981,61.8847081 86.8838876,62.6084343 87.4265392,62.3076084 C87.9700632,62.0059104 88.4525171,62.9110042 88.7552504,62.7305086 C89.0571112,62.5473972 90.3247523,62.5491411 90.6266132,62.850839 C90.9302189,63.1525369 90.9877993,62.5473972 90.9877993,62.186406 C90.9877993,61.8245429 92.19786,60.8601559 92.19786,60.8601559 C92.19786,60.8601559 93.0205616,60.815686 93.5108674,60.5950803 C93.4009412,60.4381276 93.3084636,60.1808996 93.2072617,59.7414322 C92.9821747,58.7447827 92.6192437,58.1579541 92.891442,57.839689 C93.1627678,57.5222958 93.9322512,58.2468939 94.3388037,58.0663984 C94.7479734,57.8859028 95.4720905,57.5222958 95.3813578,57.1150908 C95.290625,56.7078858 94.6144916,56.1663991 94.2524331,56.0757153 Z'
												className='shape'
											></path>
											<path
												fillRule='nonzero'
												stroke='#FFFFFF'
												strokeWidth='0.2'
												d='M67.528151,83.9566136 C67.110257,84.5207712 66.7002148,85.2375218 66.7464536,85.4703349 C66.7665195,85.5732261 66.7316223,85.8060392 66.6609554,86.1033773 C67.1992449,86.5376131 67.7209581,86.9753366 68.1048274,87.1750153 C68.1432143,87.1950704 68.1763667,87.2125096 68.2130088,87.2316927 C68.3325317,86.560284 68.7949197,85.8688203 69.1299329,85.7886 C68.6623103,85.212235 67.972218,84.4013128 67.528151,83.9566136 Z'
												className='shape'
											></path>
										</svg>
									</div>
								</div>
							</div>
							<div className='w-1/4 pl-6'>
								<div className='mb-2 flex justify-between items-center'>
									<h4 className='flex justify-start items-center font-display text-h2-head text-cPurple-800 font-semibold tracking-tight'>
										<svg
											xmlns='http://www.w3.org/2000/svg'
											fill='none'
											viewBox='0 0 24 24'
											strokeWidth='1.5'
											stroke='currentColor'
											className='w-7 h-7 mr-2 text-cPurple-500'
										>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												d='M12.75 3.03v.568c0 .334.148.65.405.864l1.068.89c.442.369.535 1.01.216 1.49l-.51.766a2.25 2.25 0 0 1-1.161.886l-.143.048a1.107 1.107 0 0 0-.57 1.664c.369.555.169 1.307-.427 1.605L9 13.125l.423 1.059a.956.956 0 0 1-1.652.928l-.679-.906a1.125 1.125 0 0 0-1.906.172L4.5 15.75l-.612.153M12.75 3.031a9 9 0 0 0-8.862 12.872M12.75 3.031a9 9 0 0 1 6.69 14.036m0 0-.177-.529A2.25 2.25 0 0 0 17.128 15H16.5l-.324-.324a1.453 1.453 0 0 0-2.328.377l-.036.073a1.586 1.586 0 0 1-.982.816l-.99.282c-.55.157-.894.702-.8 1.267l.073.438c.08.474.49.821.97.821.846 0 1.598.542 1.865 1.345l.215.643m5.276-3.67a9.012 9.012 0 0 1-5.276 3.67m0 0a9 9 0 0 1-10.275-4.835M15.75 9c0 .896-.393 1.7-1.016 2.25'
											/>
										</svg>
										World
									</h4>
									<div className=''>
										<a
											href='https://pricemonitor.cogvio.com/datasources'
											className='inline-flex justify-end items-center text-label font-semibold text-cPurple-400 underline underline-offset-2 transition hover:text-cPurple-800'
											target='_blank'
										>
											All datasources
											<svg
												xmlns='http://www.w3.org/2000/svg'
												viewBox='0 0 20 20'
												fill='currentColor'
												className='w-3 h-3 ml-0.5'
											>
												<path
													fillRule='evenodd'
													d='M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Z'
													clipRule='evenodd'
												/>
												<path
													fillRule='evenodd'
													d='M6.194 12.753a.75.75 0 0 0 1.06.053L16.5 4.44v2.81a.75.75 0 0 0 1.5 0v-4.5a.75.75 0 0 0-.75-.75h-4.5a.75.75 0 0 0 0 1.5h2.553l-9.056 8.194a.75.75 0 0 0-.053 1.06Z'
													clipRule='evenodd'
												/>
											</svg>
										</a>
									</div>
								</div>
								<ul className='space-y-4 list-none'>
									{Array.from(groupListWorldArray.entries()).map((entry) => {
										const [name, list] = entry;
										return (
											<CountryItem
												key={name.replace(/[\s,]+/g, '-').toLowerCase()}
												name={name}
												sourcelist={list}
												variant='blue'
											/>
										);
									})}
								</ul>
							</div>
						</div>
					)}

					{!showMap && (
						<div className='block w-full'>
							<div className='w-full mb-10'>
								<div className='mb-2 flex justify-between items-center'>
									<h4 className='flex justify-start items-center font-display text-h2-head text-cPurple-800 font-semibold tracking-tight'>
										<svg
											xmlns='http://www.w3.org/2000/svg'
											fill='none'
											viewBox='0 0 24 24'
											strokeWidth='1.5'
											stroke='currentColor'
											className='w-7 h-7 mr-2 text-cPurple-500'
										>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												d='m20.893 13.393-1.135-1.135a2.252 2.252 0 0 1-.421-.585l-1.08-2.16a.414.414 0 0 0-.663-.107.827.827 0 0 1-.812.21l-1.273-.363a.89.89 0 0 0-.738 1.595l.587.39c.59.395.674 1.23.172 1.732l-.2.2c-.212.212-.33.498-.33.796v.41c0 .409-.11.809-.32 1.158l-1.315 2.191a2.11 2.11 0 0 1-1.81 1.025 1.055 1.055 0 0 1-1.055-1.055v-1.172c0-.92-.56-1.747-1.414-2.089l-.655-.261a2.25 2.25 0 0 1-1.383-2.46l.007-.042a2.25 2.25 0 0 1 .29-.787l.09-.15a2.25 2.25 0 0 1 2.37-1.048l1.178.236a1.125 1.125 0 0 0 1.302-.795l.208-.73a1.125 1.125 0 0 0-.578-1.315l-.665-.332-.091.091a2.25 2.25 0 0 1-1.591.659h-.18c-.249 0-.487.1-.662.274a.931.931 0 0 1-1.458-1.137l1.411-2.353a2.25 2.25 0 0 0 .286-.76m11.928 9.869A9 9 0 0 0 8.965 3.525m11.928 9.868A9 9 0 1 1 8.965 3.525'
											/>
										</svg>
										Europe
									</h4>
									<div className=''>
										<a
											href='/datasources'
											className='inline-flex justify-end items-center text-label font-semibold text-cPurple-400 underline underline-offset-2 transition hover:text-cPurple-800'
											target='_blank'
										>
											All datasources
											<svg
												xmlns='http://www.w3.org/2000/svg'
												viewBox='0 0 20 20'
												fill='currentColor'
												className='w-3 h-3 ml-0.5'
											>
												<path
													fillRule='evenodd'
													d='M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Z'
													clipRule='evenodd'
												/>
												<path
													fillRule='evenodd'
													d='M6.194 12.753a.75.75 0 0 0 1.06.053L16.5 4.44v2.81a.75.75 0 0 0 1.5 0v-4.5a.75.75 0 0 0-.75-.75h-4.5a.75.75 0 0 0 0 1.5h2.553l-9.056 8.194a.75.75 0 0 0-.053 1.06Z'
													clipRule='evenodd'
												/>
											</svg>
										</a>
									</div>
								</div>
								<ul className='grid grid-cols-4 gap-4 list-none'>
									{Array.from(groupListEuropeArray.entries()).map((entry) => {
										const [name, list] = entry;
										return (
											<CountryItem
												key={name.replace(/[\s,]+/g, '-').toLowerCase()}
												name={name}
												sourcelist={list}
												variant='white'
											/>
										);
									})}
								</ul>
							</div>
							<div className='w-full'>
								<div className='mb-2 flex justify-between items-center'>
									<h4 className='flex justify-start items-center font-display text-h2-head text-cPurple-800 font-semibold tracking-tight'>
										<svg
											xmlns='http://www.w3.org/2000/svg'
											fill='none'
											viewBox='0 0 24 24'
											strokeWidth='1.5'
											stroke='currentColor'
											className='w-7 h-7 mr-2 text-cPurple-500'
										>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												d='M12.75 3.03v.568c0 .334.148.65.405.864l1.068.89c.442.369.535 1.01.216 1.49l-.51.766a2.25 2.25 0 0 1-1.161.886l-.143.048a1.107 1.107 0 0 0-.57 1.664c.369.555.169 1.307-.427 1.605L9 13.125l.423 1.059a.956.956 0 0 1-1.652.928l-.679-.906a1.125 1.125 0 0 0-1.906.172L4.5 15.75l-.612.153M12.75 3.031a9 9 0 0 0-8.862 12.872M12.75 3.031a9 9 0 0 1 6.69 14.036m0 0-.177-.529A2.25 2.25 0 0 0 17.128 15H16.5l-.324-.324a1.453 1.453 0 0 0-2.328.377l-.036.073a1.586 1.586 0 0 1-.982.816l-.99.282c-.55.157-.894.702-.8 1.267l.073.438c.08.474.49.821.97.821.846 0 1.598.542 1.865 1.345l.215.643m5.276-3.67a9.012 9.012 0 0 1-5.276 3.67m0 0a9 9 0 0 1-10.275-4.835M15.75 9c0 .896-.393 1.7-1.016 2.25'
											/>
										</svg>
										World
									</h4>
									<div className=''>
										<a
											href='/datasources'
											className='inline-flex justify-end items-center text-label font-semibold text-cPurple-400 underline underline-offset-2 transition hover:text-cPurple-800'
											target='_blank'
										>
											All datasources
											<svg
												xmlns='http://www.w3.org/2000/svg'
												viewBox='0 0 20 20'
												fill='currentColor'
												className='w-3 h-3 ml-0.5'
											>
												<path
													fillRule='evenodd'
													d='M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Z'
													clipRule='evenodd'
												/>
												<path
													fillRule='evenodd'
													d='M6.194 12.753a.75.75 0 0 0 1.06.053L16.5 4.44v2.81a.75.75 0 0 0 1.5 0v-4.5a.75.75 0 0 0-.75-.75h-4.5a.75.75 0 0 0 0 1.5h2.553l-9.056 8.194a.75.75 0 0 0-.053 1.06Z'
													clipRule='evenodd'
												/>
											</svg>
										</a>
									</div>
								</div>
								<ul className='grid grid-cols-4 gap-4 list-none'>
									{Array.from(groupListWorldArray.entries()).map((entry) => {
										const [name, list] = entry;
										return (
											<CountryItem
												key={name.replace(/[\s,]+/g, '-').toLowerCase()}
												name={name}
												sourcelist={list}
												variant='white'
											/>
										);
									})}
								</ul>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
